import React, { useEffect, useMemo, useState } from 'react';
import { bp_demographics, bp_mentions } from '../../../services';
import CommonButton from '../../../Components/CommonButton';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import StatusBadge from '../../../Components/StatusBadge';
import AddOnCard from '../../AddOns/AddOnCard';
import { lightTheme as T } from '../../../Constants/Colors';
import { Icon } from './_icons';
import { Body, SubHeading } from '../../../Constants/Fonts';
import { formatNumber, timeAgo, unwrap, PLATFORM_ICON } from './_shared';
import { profileUrlFor } from './_avatarKit';

const LANGS = {
    en: ['English', 'English'], ja: ['Japanese', '日本語'], ko: ['Korean', '한국어'],
    zh: ['Chinese', '中文'], es: ['Spanish', 'Español'], pt: ['Portuguese', 'Português'],
    fr: ['French', 'Français'], de: ['German', 'Deutsch'], it: ['Italian', 'Italiano'],
    nl: ['Dutch', 'Nederlands'], sv: ['Swedish', 'Svenska'], no: ['Norwegian', 'Norsk'],
    da: ['Danish', 'Dansk'], fi: ['Finnish', 'Suomi'], pl: ['Polish', 'Polski'],
    cs: ['Czech', 'Čeština'], el: ['Greek', 'Ελληνικά'], tr: ['Turkish', 'Türkçe'],
    ru: ['Russian', 'Русский'], uk: ['Ukrainian', 'Українська'], ar: ['Arabic', 'العربية'],
    he: ['Hebrew', 'עברית'], hi: ['Hindi', 'हिन्दी'], bn: ['Bengali', 'বাংলা'],
    th: ['Thai', 'ไทย'], vi: ['Vietnamese', 'Tiếng Việt'], id: ['Indonesian', 'Bahasa Indonesia'],
    ms: ['Malay', 'Bahasa Melayu'], tl: ['Tagalog', 'Tagalog'], ur: ['Urdu', 'اردو'],
    fa: ['Persian', 'فارسی'],
    unknown: ['Unknown', 'Unknown'],
};
export const langName = l => (LANGS[l] ? LANGS[l][0] : String(l || '').toUpperCase());
const langNative = l => (LANGS[l] ? LANGS[l][1] : l);
export const langTitle = l => (langName(l) === langNative(l) ? langName(l) : `${langName(l)} · ${langNative(l)}`);

const PLATFORM_LABEL = {
    x: 'X', twitter: 'X', linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok',
    reddit: 'Reddit', bluesky: 'Bluesky', facebook: 'Facebook', instagram: 'Instagram',
    vimeo: 'Vimeo', telegram: 'Telegram', blog: 'Blogs & news', web: 'Web',
};
export const platformLabel = p => PLATFORM_LABEL[String(p || '').toLowerCase()]
    || (p ? String(p).charAt(0).toUpperCase() + String(p).slice(1) : 'Other');

const TOP_N = 6;
const PREVIEW_POSTS = 3;
const slotOf = i => (i < TOP_N ? i + 1 : 'other');

const FALLBACK_SCAN = 100;

// Latest posts in one language. The row count (bp_demographics) and the posts
// (bp_mentions) can disagree, so an empty result falls back to scanning the
// latest FALLBACK_SCAN posts by their own `language`.
function LanguagePosts({ lang, filterParams, count }) {
    const [posts, setPosts] = useState(null);
    useEffect(() => {
        let alive = true;
        const latest = extra => bp_mentions({ ...filterParams, page: 1, sort: '-posted_at', ...extra })
            .then(r => (unwrap(r) || {}).results || []);
        latest({ language: lang, page_size: PREVIEW_POSTS })
            .then(found => {
                if (found.length > 0 || !count) return found;
                return latest({ language: undefined, page_size: FALLBACK_SCAN })
                    .then(all => all
                        .filter(m => String(m.language || '').toLowerCase() === lang)
                        .slice(0, PREVIEW_POSTS));
            })
            .then(found => { if (alive) setPosts(found); })
            .catch(() => { if (alive) setPosts([]); });
        return () => { alive = false; };
    }, [lang, filterParams, count]);

    if (posts == null) return <CommonLoader label="Loading posts…" />;
    if (posts.length === 0) {
        return (
            <Body variant={2} className="bm_lang_empty">
                {count
                    ? `${formatNumber(count)} ${count === 1 ? 'mention is' : 'mentions are'} counted as ${langName(lang)}, but ${count === 1 ? 'it' : 'they'} could not be loaded for preview.`
                    : 'No posts to preview.'}
            </Body>
        );
    }
    return (
        <div className="bm_lang_posts">
            {posts.map(m => {
                const href = profileUrlFor(m.handle, m.platform) || m.url;
                const p = String(m.platform || '').toLowerCase();
                const body = (
                    <>
                        <div className="bm_lang_post_head">
                            <span className="bm_lang_post_platform" title={platformLabel(p)} aria-hidden="true">
                                {PLATFORM_ICON[p] || platformLabel(p).charAt(0)}
                            </span>
                            <Body as="span" variant={2} weight="semibold" className="bm_lang_post_handle">{m.handle || '(no handle)'}</Body>
                            <Body as="span" variant={2} className="bm_lang_post_time">{timeAgo(m.posted_at)}</Body>
                        </div>
                        <Body variant={2} className="bm_lang_post_text">{m.text}</Body>
                    </>
                );
                return href ? (
                    <a key={m.id} className="bm_lang_post cmn_elevated_card" href={href} target="_blank" rel="noopener noreferrer"
                        title="Open on the source platform">{body}</a>
                ) : (
                    <div key={m.id} className="bm_lang_post cmn_elevated_card">{body}</div>
                );
            })}
        </div>
    );
}

function Kpi({ label, value, sub, icon }) {
    return (
        <AddOnCard
            className="bm_lang_kpi"
            align="flex-start"
            label={
                <div className="bm_lang_kpi_text">
                    <div className="bm_lang_kpi_head">
                        <span className="bm_lang_kpi_icon" aria-hidden="true">
                            <Icon name={icon} size={14} color={T.primary} />
                        </span>
                        <Body as="span" variant={2} weight="semibold" className="bm_lang_kpi_label">{label}</Body>
                    </div>
                    <SubHeading as="div" className="bm_lang_kpi_value" title={String(value)}>{value}</SubHeading>
                    {sub && <Body as="div" variant={2} className="bm_lang_kpi_sub" title={sub}>{sub}</Body>}
                </div>
            }
            action={null}
        />
    );
}

export function LanguageBreakdownModal({ data, filterParams, activeLanguages, onShowInFeed, onClose }) {
    const { languages, platform_x_lang: pxl, total } = data;
    const [openLang, setOpenLang] = useState(null);

    const byLang = useMemo(() => {
        const m = {};
        (pxl || []).forEach(r => {
            (m[r.lang] = m[r.lang] || []).push({ platform: r.platform, count: r.count });
        });
        Object.values(m).forEach(list => list.sort((a, b) => b.count - a.count));
        return m;
    }, [pxl]);

    const nonEn = languages.filter(l => l.lang !== 'en' && l.lang !== 'unknown');
    const nonEnCount = nonEn.reduce((s, l) => s + l.count, 0);
    const topNonEn = nonEn[0];
    const multilingual = useMemo(() => {
        const perPlatform = {};
        (pxl || []).forEach(r => {
            if (r.count > 0 && r.lang !== 'unknown') (perPlatform[r.platform] = perPlatform[r.platform] || new Set()).add(r.lang);
        });
        const best = Object.entries(perPlatform).sort((a, b) => b[1].size - a[1].size)[0];
        return best ? { platform: best[0], langs: best[1].size } : null;
    }, [pxl]);
    const maxCount = Math.max(1, ...languages.map(l => l.count));

    return (
        <CommonModal
            show={true}
            onHide={onClose}
            size="xl"
            title="Language breakdown"
            subtitle={`${formatNumber(total)} mentions matching your current filters. Open a language to read its latest posts.`}
        >
            <div className="bm_lang_modal">
                <div className="bm_lang_kpis">
                    <Kpi icon="globe" label="Languages" value={languages.length} sub={`across ${formatNumber(total)} mentions`} />
                    <Kpi icon="flag" label="Non-English" value={formatNumber(nonEnCount)}
                        sub={`${total ? Math.round((nonEnCount / total) * 100) : 0}% of mentions`} />
                    <Kpi icon="check" label="Top non-English" value={topNonEn ? langName(topNonEn.lang) : '-'}
                        sub={topNonEn ? `${formatNumber(topNonEn.count)} mentions` : 'all mentions are English'} />
                    <Kpi icon="shield" label="Most multilingual" value={multilingual ? platformLabel(multilingual.platform) : '-'}
                        sub={multilingual ? `${multilingual.langs} language${multilingual.langs === 1 ? '' : 's'}` : 'no data'} />
                </div>

                <div className="bm_lang_list">
                    {languages.map((l, i) => {
                        const isOpen = openLang === l.lang;
                        const share = total ? (l.count / total) * 100 : 0;
                        const platforms = byLang[l.lang] || [];
                        const filtered = activeLanguages.includes(l.lang);
                        return (
                            <div key={l.lang} className={`bm_lang_item cmn_elevated_card ${isOpen ? 'is-open' : ''}`}>
                                <button type="button" className="bm_lang_item_head"
                                    aria-expanded={isOpen}
                                    onClick={() => setOpenLang(isOpen ? null : l.lang)}>
                                    <span className={`bm_lang_swatch bm_lang_slot-${slotOf(i)}`} aria-hidden="true" />
                                    <span className="bm_lang_item_name">
                                        <Body as="span" variant={2} weight="semibold" className="bm_lang_item_title">{langName(l.lang)}</Body>
                                        {langNative(l.lang) !== langName(l.lang) && (
                                            <Body as="span" variant={2} className="bm_lang_item_native">{langNative(l.lang)}</Body>
                                        )}
                                    </span>
                                    <span className="bm_lang_item_bar" aria-hidden="true">
                                        <span className={`bm_lang_item_fill bm_lang_slot-${slotOf(i)}`}
                                            style={{ width: `${(l.count / maxCount) * 100}%` }} />
                                    </span>
                                    <Body as="span" variant={2} weight="semibold" className="bm_lang_item_count">{formatNumber(l.count)}</Body>
                                    <Body as="span" variant={2} className="bm_lang_item_share">{share.toFixed(1)}%</Body>
                                    <span className="bm_lang_item_caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                                </button>
                                {platforms.length > 0 && (
                                    <div className="bm_lang_item_platforms">
                                        {platforms.map(p => (
                                            <StatusBadge key={p.platform} variant="neutral" dot={false} transform="none">
                                                {platformLabel(p.platform)} · {formatNumber(p.count)}
                                            </StatusBadge>
                                        ))}
                                    </div>
                                )}
                                {isOpen && (
                                    <div className="bm_lang_item_body">
                                        <LanguagePosts lang={l.lang} filterParams={filterParams} count={l.count} />
                                        <CommonButton
                                            text={filtered ? `Remove ${langName(l.lang)} filter` : `Show ${langName(l.lang)} in feed`}
                                            variant={filtered ? 'secondary' : 'primary'}
                                            onClick={() => onShowInFeed(l.lang)} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </CommonModal>
    );
}

export function useLanguageData(filterParams) {
    const [data, setData] = useState({ total: 0, languages: [], platform_x_lang: [] });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        bp_demographics(filterParams)
            .then(r => {
                if (!alive) return;
                const d = unwrap(r) || {};
                setData({ total: d.total || 0, languages: d.languages || [], platform_x_lang: d.platform_x_lang || [] });
            })
            .catch(() => { if (alive) setData({ total: 0, languages: [], platform_x_lang: [] }); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [filterParams]);
    return { data, loading };
}
